import { useState } from 'react';

import { ATSTabbedJobViewAutomationsTabTestIds } from 'data-testids/ATS';
import { AutomationContent } from './components/AutomationContent';
import { AutomationModal } from 'features/Automations/AutomationModal';
import { AutomationProvider } from 'features/Automations/context/AutomationContext';
import { AutomationsTabProps } from './types';
import { AutomationTitle } from './styles';
import { ReferenceCheckSection } from './components/ReferenceCheckSection';
import { sortAndCombineQuestionsAndAttachments } from '../QuestionsTab/utils';
import { transformOptions, filterGeneralAutomations } from 'features/Automations/utils';
import { useQueryGetQuestionsByJobId } from '../QuestionsTab/queries';
import { Box, Typography, useTheme } from '@mui/material';
import { useQueryGetAutomationsListData } from 'features/Automations/api/listQueries';
import {
  getAxiosErrorMessageByStatusCode,
  DEFAULT_AUTOMATION_ERROR_MESSAGE,
} from 'hooks/errorHandling/errorHandlingHooks';
import { Alert } from 'sharedComponents/Alert';

export const AutomationsTab: React.FC<AutomationsTabProps> = ({ jobId }) => {
  const [showModal, setShowModal] = useState(false);
  const theme = useTheme();
  const { data: questions, isLoading } = useQueryGetQuestionsByJobId(jobId);
  const sortedQuestions = questions && sortAndCombineQuestionsAndAttachments(questions);
  const transformedQuestions = transformOptions(sortedQuestions || []);
  const { data: automations, error } = useQueryGetAutomationsListData(jobId);
  const generalAutomations = automations ? filterGeneralAutomations(automations) : [];
  const hasAutomations = generalAutomations.length > 0;

  const automationErrorMessages = {
    400: DEFAULT_AUTOMATION_ERROR_MESSAGE,
  };

  const errorMessage = getAxiosErrorMessageByStatusCode(error, automationErrorMessages);

  if (errorMessage) {
    return (
      <Alert dataTestId={ATSTabbedJobViewAutomationsTabTestIds.ERROR_MESSAGE} type="error">
        {errorMessage ?? 'Something went wrong'}
      </Alert>
    );
  }

  return (
    <AutomationProvider
      onClose={() => setShowModal(false)}
      open={showModal}
      questions={transformedQuestions}
      roleId={jobId}
    >
      <AutomationTitle
        data-testid={ATSTabbedJobViewAutomationsTabTestIds.AUTOMATIONS_TITLE}
        variant="h2"
      >
        Automations
      </AutomationTitle>
      <Box data-testid={ATSTabbedJobViewAutomationsTabTestIds.AUTOMATIONS_CONTENT}>
        <Typography
          variant="h3"
          sx={{
            color: theme.palette.text.primary,
            fontWeight: theme.typography.fontWeightMedium,
            marginBottom: theme.spacing(1),
          }}
        >
          Pre-Screening
        </Typography>
        <Typography sx={{ pb: 2 }}>
          Tag candidates and move applications automatically based on a candidate&apos;s application
          answers.
        </Typography>
        <AutomationContent
          hasAutomations={hasAutomations}
          isLoading={isLoading}
          onOpenClick={() => setShowModal(true)}
        />
        <ReferenceCheckSection jobId={jobId} />
      </Box>
      <AutomationModal />
    </AutomationProvider>
  );
};
